import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

// ContactPanel — renders inside the CRT when the viewer tunes to the
// ★ CONTACT virtual channel. Same scartlead.tv "broadcast slate" framing
// as MaintenanceOverlay / NoticeOverlay so the message reads as a
// dedicated station identification, not a modal grafted on top.
//
// Fields: Name, Email, Message + a honeypot `website` field that should
// stay invisible to humans (off-screen) but irresistible to naive bots —
// the backend silently drops any submission where it's non-empty.

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export default function ContactPanel() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState(""); // honeypot — leave empty
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error("MISSING TRANSMISSION", { description: "Name, email and message are all required." });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, { name, email, message, website });
      setSent(true);
      toast.success("SIGNAL RECEIVED", { description: "Your message reached the station." });
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "Unknown error";
      toast.error("TRANSMISSION FAILED", { description: detail });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="absolute inset-0 z-30 bg-black text-[var(--phosphor)] flex flex-col items-center justify-center overflow-hidden py-8"
      style={{ fontFamily: "JetBrains Mono, monospace" }}
      data-testid="contact-panel"
    >
      {/* SMPTE colour-bars strip — same brand seal as every other in-CRT
          panel so the eye reads this as "official station broadcast" */}
      <div className="absolute top-0 left-0 right-0 h-3 flex">
        {["#FFFFFF", "#FFFF00", "#00FFFF", "#00FF00", "#FF00FF", "#FF0000", "#0000FF", "#000000"].map((c, i) => (
          <div key={i} className="flex-1" style={{ backgroundColor: c }} />
        ))}
      </div>

      <div className="relative w-full max-w-2xl max-h-full flex flex-col text-center px-6 min-h-0">
        <div className="text-[10px] md:text-xs tracking-[0.5em] opacity-70 mb-3 shrink-0">
          ┌─ SCARTLEAD.TV CONTACT ─┐
        </div>
        <div className="text-xl md:text-3xl tracking-[0.12em] chromatic mb-4 phosphor-glow shrink-0">
          {sent ? "TRANSMISSION SENT" : "REACH THE STATION"}
        </div>

        {sent ? (
          <div className="flex-1 min-h-0 overflow-y-auto scrollbar-crt flex flex-col items-center justify-center">
            <p className="text-sm md:text-base leading-relaxed opacity-90 mb-6 px-4">
              Thanks for tuning in. Your message has been picked up by the
              station and the operator will respond when next on-air.
            </p>
            <button
              onClick={() => { setSent(false); setName(""); setEmail(""); setMessage(""); }}
              data-testid="contact-send-another"
              className="px-6 py-2 border border-[var(--phosphor)] text-[var(--phosphor)] tracking-[0.25em] text-xs hover:bg-[var(--phosphor)] hover:text-black transition-colors phosphor-glow"
            >
              ▶ SEND ANOTHER
            </button>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="flex-1 min-h-0 overflow-y-auto scrollbar-crt flex flex-col gap-3 text-left"
            data-testid="contact-form"
          >
            <label className="flex flex-col gap-1">
              <span className="text-[10px] tracking-[0.3em] opacity-70">YOUR NAME</span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={120}
                data-testid="contact-name"
                className="bg-black border border-[var(--phosphor)] text-[var(--phosphor)] p-2 text-sm outline-none focus:border-white"
                disabled={submitting}
                autoComplete="name"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] tracking-[0.3em] opacity-70">YOUR EMAIL</span>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                maxLength={200}
                data-testid="contact-email"
                className="bg-black border border-[var(--phosphor)] text-[var(--phosphor)] p-2 text-sm outline-none focus:border-white"
                disabled={submitting}
                autoComplete="email"
              />
            </label>
            <label className="flex flex-col gap-1 flex-1 min-h-0">
              <span className="text-[10px] tracking-[0.3em] opacity-70">MESSAGE</span>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={4000}
                rows={5}
                data-testid="contact-message"
                className="bg-black border border-[var(--phosphor)] text-[var(--phosphor)] p-2 text-sm outline-none focus:border-white resize-none"
                disabled={submitting}
              />
            </label>

            {/* Honeypot — hidden from humans via aria-hidden + position
                off-screen; bots that crawl every input will fill it and
                trip the backend's silent-drop filter. tabIndex=-1 stops
                keyboard users from landing here. */}
            <div
              aria-hidden="true"
              style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", overflow: "hidden" }}
            >
              <label>
                Website (leave empty)
                <input
                  type="text"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </label>
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit"
              className="mt-2 mx-auto px-10 py-2 border-2 border-[var(--phosphor)] text-[var(--phosphor)] tracking-[0.3em] text-sm hover:bg-[var(--phosphor)] hover:text-black transition-colors phosphor-glow font-bold disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
            >
              {submitting ? "TRANSMITTING…" : "▶ TRANSMIT"}
            </button>
          </form>
        )}

        <div className="mt-3 text-[10px] opacity-50 tracking-[0.3em] shrink-0">
          MESSAGES ARE READ BY THE STATION OPERATOR
        </div>
      </div>

      {/* Cabinet corner labels — match the other in-CRT panels */}
      <div className="absolute bottom-3 left-4 text-[9px] opacity-50 tracking-widest">
        CRT-1986
      </div>
      <div className="absolute bottom-3 right-4 text-[9px] opacity-50 tracking-widest">
        SCARTLEAD.TV
      </div>
    </div>
  );
}
